// Morrow — shared per-run step view. Given a runId, fetches the run's steps and —
// while the run is still "running" — polls every ~3s so the stepper fills in live.
// Used by the workflow SETUP page (editor + runs tab), the workflows LIST page, and
// the Activities detail sheet. When a workflow graph is available each step's nodeId
// is merged for a friendly label/kind; with no graph it falls back to the backend
// step label/kind so it still renders.
import { Link } from "@tanstack/react-router";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Loader2Icon,
  MessagesSquareIcon,
  MinusIcon,
  RepeatIcon,
  RotateCwIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  fetchWorkflowRunSteps,
  resumeWorkflowRun,
  type WorkflowRunStepLite,
} from "../../morrowAgents";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { type WfGraph, type WfNodeData } from "./WorkflowCanvas";

/** Status icon for one run step — spinner (running) / check (ok) / dash (skipped) / x (failed). */
export function StepStatusIcon({ status }: { status: string }) {
  if (status === "running")
    return <Loader2Icon className="size-3.5 shrink-0 animate-spin text-primary" />;
  if (status === "ok") return <CheckIcon className="size-3.5 shrink-0 text-emerald-500" />;
  if (status === "failed") return <XIcon className="size-3.5 shrink-0 text-red-500" />;
  if (status === "skipped")
    return <MinusIcon className="size-3.5 shrink-0 text-muted-foreground/50" />;
  return <span className="size-3.5 shrink-0" />;
}

/**
 * Per-node progress for ONE run, rendered as an ordered stepper. Fetches the run's steps and —
 * while the run is still "running" — polls every ~3s so the stepper fills in live. Each step's
 * nodeId is merged against the workflow graph for a friendly label/kind; for-each loops show
 * "item N/total".
 */
export function RunStepsPanel({
  runId,
  graph,
  runStatus,
  threadId,
}: {
  runId: string;
  graph: WfGraph | null;
  runStatus: string;
  /** The run's own thread. Given one, the panel offers to open the conversation — the same jump
   * Activities gives you, so a run is readable from wherever you found it. */
  threadId?: string | null;
}) {
  const [steps, setSteps] = useState<readonly WorkflowRunStepLite[]>([]);
  const [loading, setLoading] = useState(true);
  // Set once the user presses Resume: polls live until the parent reports a new run status.
  const [resumed, setResumed] = useState(false);
  const [resumeState, setResumeState] = useState<"idle" | "starting" | "error">("idle");
  const [allPasses, setAllPasses] = useState(false);
  // For each produces one row per item (often dozens) — collapsed by default behind a toggle so
  // the run panel stays a compact bar; the tally above already summarizes them. Keyed by node id.
  const [itemsOpen, setItemsOpen] = useState<ReadonlySet<string>>(() => new Set());
  const toggleItems = (key: string) =>
    setItemsOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const environmentId = usePrimaryEnvironmentId();
  useEffect(() => {
    setResumed(false);
    setResumeState("idle");
  }, [runId, runStatus]);
  const liveStatus = resumed ? "running" : runStatus;
  const onResume = async () => {
    setResumeState("starting");
    const ok = await resumeWorkflowRun(runId);
    setResumeState(ok ? "idle" : "error");
    if (ok) setResumed(true);
  };
  // Which step rows are expanded to reveal their output (`detail`). Keyed by step id.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      const rows = await fetchWorkflowRunSteps(runId);
      if (!active) return;
      setSteps(rows);
      setLoading(false);
      // Keep polling only while the run is still in flight.
      if (liveStatus === "running") timer = setTimeout(() => void tick(), 3000);
    };
    void tick();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [runId, liveStatus]);

  // How many times round the loop this run went, and which steps to show for it.
  const passCount = useMemo(() => steps.reduce((max, s) => Math.max(max, s.pass ?? 1), 1), [steps]);
  /**
   * Does this run belong to a LOOPING workflow? The server stamps `pass` on every step of a graph
   * that has a loop edge — including pass 1 — and on none of the steps of a graph that doesn't.
   * So a stamp anywhere means "this is a loop", which is what lets the view say so even when the
   * loop only went round once. (Runs recorded before that change stamped pass 2 upwards only, and
   * read here as a loop from their second pass on — the honest answer for the data they hold.)
   */
  const looped = useMemo(() => steps.some((s) => s.pass != null), [steps]);
  /**
   * The steps grouped into the passes they ran in, in order. A loop runs the same nodes over and
   * over, and a flat list of every pass's rows reads as one long run of repeated steps — which is
   * exactly how a loop became invisible. Grouping is what shows the shape: pass 1, pass 2, ….
   * The leading `pass: null` group is the pre-loop part of an older run (and the whole of a run
   * with no loop at all).
   */
  const groups = useMemo(() => {
    const byPass = new Map<number | null, WorkflowRunStepLite[]>();
    for (const s of steps) {
      const key = s.pass ?? null;
      const list = byPass.get(key);
      if (list) list.push(s);
      else byPass.set(key, [s]);
    }
    return [...byPass.entries()]
      .map(([pass, rows]) => ({ pass, rows }))
      .sort((a, b) => (a.pass ?? 0) - (b.pass ?? 0));
  }, [steps]);
  const shownGroups = useMemo(() => {
    if (allPasses || passCount <= 1) return groups;
    // Keep the LAST pass, plus anything that ran before the loop (no pass stamped).
    return groups.filter((g) => g.pass === passCount || g.pass === null);
  }, [groups, allPasses, passCount]);

  const nodeById = useMemo(() => {
    const map = new Map<string, WfNodeData>();
    for (const node of graph?.nodes ?? []) map.set(node.id, node.data as WfNodeData);
    return map;
  }, [graph]);

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 pl-1 pt-1 text-[11px] text-muted-foreground/60">
        <Loader2Icon className="size-3 animate-spin" /> Loading steps…
      </div>
    );
  }
  if (steps.length === 0) {
    return (
      <div className="pl-1 pt-1 text-[11px] text-muted-foreground/60">
        No per-step detail for this run.
      </div>
    );
  }

  // Resumable: the run has ended but didn't fully succeed (it failed / was stopped, or an item
  // inside it failed). Resume continues in place and skips the items already done.
  const canResume =
    liveStatus !== "running" && (runStatus !== "ok" || steps.some((s) => s.status === "failed"));

  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      {threadId && environmentId ? (
        <Link
          to="/$environmentId/$threadId"
          params={{ environmentId, threadId }}
          className="inline-flex w-fit items-center gap-1 rounded-md border border-border/70 bg-card px-2 py-0.5 text-[11px] font-medium text-foreground transition hover:bg-muted"
        >
          <MessagesSquareIcon className="size-3" />
          Open conversation
        </Link>
      ) : null}
      {canResume ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={resumeState === "starting"}
            onClick={() => void onResume()}
            className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-card px-2 py-0.5 text-[11px] font-medium text-foreground transition hover:bg-muted disabled:opacity-60"
          >
            {resumeState === "starting" ? (
              <Loader2Icon className="size-3 animate-spin" />
            ) : (
              <RotateCwIcon className="size-3" />
            )}
            Resume
          </button>
          <span className="text-[10px] text-muted-foreground/70">
            {resumeState === "error"
              ? "Couldn't resume this run. Try again in a moment."
              : "Continues where it stopped and skips the items already done."}
          </span>
        </div>
      ) : null}
      {/* A loop runs the same steps over and over, so once there is more than one pass the flat
          list becomes dozens of near-identical rows. Collapse to the latest pass by default and
          offer the rest, which is the difference between a readable run and a wall. With a single
          pass there is nothing to collapse — but the run must still SAY it was a loop, otherwise
          "7 steps" is indistinguishable from a workflow that has no loop in it at all. */}
      {looped && passCount > 1 ? (
        <button
          type="button"
          onClick={() => setAllPasses((v) => !v)}
          className="flex w-fit items-center gap-1 text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
        >
          {allPasses ? (
            <ChevronDownIcon className="size-3" aria-hidden />
          ) : (
            <ChevronRightIcon className="size-3" aria-hidden />
          )}
          {allPasses
            ? `Showing all ${passCount} passes`
            : `Showing the last of ${passCount} passes`}
        </button>
      ) : null}
      {looped && passCount === 1 ? (
        <div className="flex w-fit items-center gap-1 text-[11px] text-muted-foreground">
          <RepeatIcon className="size-3" aria-hidden />
          {liveStatus === "running"
            ? "This workflow loops — on its first pass so far."
            : "This workflow loops — it went round once."}
        </div>
      ) : null}
      <ForEachTallies steps={steps} nodeById={nodeById} />
      {shownGroups.map((group) => {
        const failedInPass = group.rows.filter((s) => s.status === "failed").length;
        return (
          <div key={group.pass ?? "pre-loop"} className="flex flex-col gap-1">
            {looped && group.pass !== null ? (
              <div className="flex items-center gap-1.5 pl-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                <RepeatIcon className="size-3" aria-hidden />
                {passCount > 1 ? `Pass ${group.pass} of ${passCount}` : `Pass ${group.pass}`}
                <span className="font-normal normal-case tracking-normal text-muted-foreground/60">
                  {group.rows.length} {group.rows.length === 1 ? "step" : "steps"}
                  {failedInPass > 0 ? ` · ${failedInPass} failed` : ""}
                </span>
              </div>
            ) : null}
            <ol className="flex flex-col gap-1 rounded-lg border border-border/50 bg-muted/20 p-2">
              {(() => {
                // Split each For each node's per-item rows out and fold them behind a toggle, so a
                // 57-item run shows a one-line "57 items" control instead of 57 rows taking over.
                const rendered: React.ReactNode[] = [];
                const itemsByNode = new Map<string, WorkflowRunStepLite[]>();
                for (const s of group.rows) {
                  if (s.iteration != null) {
                    (
                      itemsByNode.get(s.nodeId) ?? itemsByNode.set(s.nodeId, []).get(s.nodeId)!
                    ).push(s);
                  }
                }
                const emittedItemsFor = new Set<string>();
                for (const s of group.rows) {
                  const isItem = s.iteration != null;
                  if (!isItem) {
                    rendered.push(
                      <StepRow
                        key={s.id}
                        step={s}
                        node={nodeById.get(s.nodeId)}
                        open={open.has(s.id)}
                        onToggle={() => toggle(s.id)}
                        environmentId={environmentId}
                        runThreadId={threadId ?? null}
                      />,
                    );
                    continue;
                  }
                  if (emittedItemsFor.has(s.nodeId)) continue;
                  emittedItemsFor.add(s.nodeId);
                  const items = itemsByNode.get(s.nodeId) ?? [];
                  const key = `${group.pass ?? "p"}:${s.nodeId}`;
                  const expanded = itemsOpen.has(key);
                  const label = "For each";
                  rendered.push(
                    <li key={key} className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => toggleItems(key)}
                        className="flex items-center gap-1.5 text-left text-[11px] text-muted-foreground hover:text-foreground"
                      >
                        {expanded ? (
                          <ChevronDownIcon className="size-3" aria-hidden />
                        ) : (
                          <ChevronRightIcon className="size-3" aria-hidden />
                        )}
                        <span className="font-medium text-foreground">{label}</span>
                        <span>
                          · {items.length} {items.length === 1 ? "item" : "items"}
                        </span>
                      </button>
                      {expanded ? (
                        <ol className="ml-4 flex max-h-72 flex-col gap-1 overflow-y-auto border-l border-border/50 pl-2">
                          {items.map((it) => (
                            <StepRow
                              key={it.id}
                              step={it}
                              node={nodeById.get(it.nodeId)}
                              open={open.has(it.id)}
                              onToggle={() => toggle(it.id)}
                              environmentId={environmentId}
                              runThreadId={threadId ?? null}
                            />
                          ))}
                        </ol>
                      ) : null}
                    </li>,
                  );
                }
                return rendered;
              })()}
            </ol>
          </div>
        );
      })}
    </div>
  );
}

/** One step of a run: its status, name, and — folded away until asked for — what it was asked and
 * what it answered. The pass it ran in is the group header's job, not the row's. */
function StepRow({
  step,
  node,
  open,
  onToggle,
  environmentId,
  runThreadId,
}: {
  step: WorkflowRunStepLite;
  node: WfNodeData | undefined;
  open: boolean;
  onToggle: () => void;
  environmentId: string | null;
  /** The run's own thread — so an item that ran on it isn't offered as a separate "tab". */
  runThreadId: string | null;
}) {
  const label = stepLabel(step, node);
  const detail = step.detail?.trim() || "";
  const prompt = step.prompt?.trim() || "";
  // A FAILED step has no reply — and that is precisely when you want to read what it was asked,
  // so the row must still open on the prompt alone.
  const hasDetail = detail.length > 0 || prompt.length > 0;
  return (
    <li className="flex flex-col gap-1">
      <button
        type="button"
        disabled={!hasDetail}
        onClick={hasDetail ? onToggle : undefined}
        aria-expanded={hasDetail ? open : undefined}
        className={`flex items-center gap-2 text-left text-[11px] ${
          hasDetail ? "cursor-pointer hover:opacity-80" : "cursor-default"
        }`}
      >
        <StepStatusIcon status={step.status} />
        <span className="min-w-0 flex-1 truncate text-foreground">{label}</span>
        {step.branch ? (
          <span className="shrink-0 rounded bg-primary/10 px-1 text-[10px] font-medium text-primary">
            → {step.branch}
          </span>
        ) : null}
        {step.iteration && step.total ? (
          <span className="shrink-0 rounded bg-primary/10 px-1 text-[10px] font-medium text-primary">
            item {step.iteration}/{step.total}
          </span>
        ) : null}
        {hasDetail ? (
          <span className="shrink-0 text-[10px] text-muted-foreground/60">
            {open ? (
              <ChevronDownIcon className="size-3" aria-hidden />
            ) : (
              <ChevronRightIcon className="size-3" aria-hidden />
            )}
          </span>
        ) : null}
        <span className="shrink-0 text-[10px] capitalize text-muted-foreground/60">
          {itemOutcome(step) ?? step.status}
        </span>
      </button>
      {hasDetail && open ? (
        <div className="ml-5 flex flex-col gap-1">
          {step.threadId && environmentId && step.threadId !== runThreadId ? (
            <Link
              to="/$environmentId/$threadId"
              params={{ environmentId, threadId: step.threadId }}
              className="inline-flex w-fit items-center gap-1 rounded-md border border-border/70 bg-card px-2 py-0.5 text-[10px] font-medium text-foreground transition hover:bg-muted"
            >
              <MessagesSquareIcon className="size-3" aria-hidden />
              Open this tab's conversation
            </Link>
          ) : null}
          {prompt ? (
            <StepBlock
              title="What it was asked"
              body={prompt}
              icon={<MessagesSquareIcon className="size-3" aria-hidden />}
            />
          ) : null}
          {detail ? (
            <StepBlock
              title="What it answered"
              body={detail}
              icon={<CheckIcon className="size-3" aria-hidden />}
            />
          ) : (
            // A step only writes its detail when it ENDS, so a step still in flight has none —
            // and saying "produced no answer" about one that is mid-work reads as a failure and
            // sent people hunting for a bug in a step that was simply still going.
            <p className="text-[10px] italic text-muted-foreground/70">
              {step.status === "running"
                ? "Still working — no answer yet."
                : "This step produced no answer."}
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}

/**
 * What a For each item ended as, in its own words ("Posted", "Pending approval", "Dry run") —
 * the item's step detail is its JSON answer. Null for any other step, or one still running.
 */
function itemOutcome(step: WorkflowRunStepLite): string | null {
  if (step.iteration == null || step.status === "running") return null;
  try {
    const parsed = JSON.parse(step.detail ?? "") as { outcome?: unknown };
    return typeof parsed.outcome === "string" && parsed.outcome.trim() ? parsed.outcome : null;
  } catch {
    return null;
  }
}

/**
 * The count for each For each block in the run: "25 items · Posted 18 · Running 3 · Failed 1".
 * The rows below are the items themselves; this is the line you read first.
 */
function ForEachTallies({
  steps,
  nodeById,
}: {
  steps: ReadonlyArray<WorkflowRunStepLite>;
  nodeById: ReadonlyMap<string, WfNodeData>;
}) {
  const blocks = useMemo(() => {
    const byNode = new Map<string, { total: number; counts: Map<string, number> }>();
    for (const s of steps) {
      if (s.iteration == null) continue;
      const entry = byNode.get(s.nodeId) ?? { total: s.total ?? 0, counts: new Map() };
      entry.total = Math.max(entry.total, s.total ?? 0);
      const key =
        s.status === "running"
          ? "Running"
          : s.status === "failed"
            ? "Failed"
            : s.status === "skipped"
              ? "Skipped"
              : (itemOutcome(s) ?? "Done");
      entry.counts.set(key, (entry.counts.get(key) ?? 0) + 1);
      byNode.set(s.nodeId, entry);
    }
    return [...byNode.entries()];
  }, [steps]);
  if (blocks.length === 0) return null;
  const tone = (key: string) =>
    key === "Failed"
      ? "bg-red-500/10 text-red-600 dark:text-red-400"
      : key === "Running"
        ? "bg-primary/10 text-primary"
        : key === "Skipped"
          ? "bg-muted text-muted-foreground"
          : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400";
  return (
    <div className="flex flex-col gap-1">
      {blocks.map(([nodeId, { total, counts }]) => {
        const started = [...counts.values()].reduce((a, b) => a + b, 0);
        const waiting = Math.max(0, total - started);
        return (
          <div
            key={nodeId}
            className="flex flex-wrap items-center gap-1 rounded-lg border border-border/50 bg-card px-2 py-1.5 text-[11px]"
          >
            <RepeatIcon className="size-3 text-muted-foreground" aria-hidden />
            <span className="font-semibold text-foreground">{"For each"}</span>
            <span className="tabular-nums text-muted-foreground">
              {total} {total === 1 ? "item" : "items"}
            </span>
            {[...counts.entries()]
              .sort((a, b) => b[1] - a[1])
              .map(([key, n]) => (
                <span
                  key={key}
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${tone(key)}`}
                >
                  {key} {n}
                </span>
              ))}
            {waiting > 0 ? (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
                Waiting {waiting}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** A step's display name, with any leading pictograph stripped.
 *
 * Older runs persisted labels like "⏰ 0 8 * * *" — the emoji is gone from the code that writes
 * them, but rows already in the database keep it, and the house rule is icons not emojis. */
function stepLabel(step: WorkflowRunStepLite, node: WfNodeData | undefined): string {
  const raw = step.label?.trim() || node?.label?.trim() || node?.kind || step.kind || "step";
  return raw.replace(/^[\p{Extended_Pictographic}\u{FE0F}\u{20E3}]+\s*/u, "").trim() || raw;
}

/** One labelled, collapsible block of a step's detail — its prompt, or its answer. */
function StepBlock({ title, body, icon }: { title: string; body: string; icon: React.ReactNode }) {
  // Prompts are long, so they start folded to a few lines and open on demand.
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-border/40 bg-background/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80 hover:text-foreground"
      >
        {icon}
        {title}
        {open ? (
          <ChevronDownIcon className="ml-auto size-3" aria-hidden />
        ) : (
          <ChevronRightIcon className="ml-auto size-3" aria-hidden />
        )}
      </button>
      <pre
        className={`overflow-auto whitespace-pre-wrap break-words px-2 pb-2 font-mono text-[10px] leading-snug text-muted-foreground ${
          open ? "max-h-72" : "max-h-14"
        }`}
      >
        {body}
      </pre>
    </div>
  );
}
